/**
 * Toast: one-line messages on the menus' surface, in a status region, centred in the page column (in
 * what an open sheet leaves of it): from `wide` in the top bar's empty middle, so it covers no row of
 * the page; below, at the foot. An action (Undo) also answers ⌘Z; `onExpire` runs when the toast
 * leaves without it, and when the page is left, so a commit is never lost with the tab.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type FocusEvent, type ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import { Kbd, modKey } from "./Kbd";

export interface ToastOptions {
  message: ReactNode;
  /** One action, usually Undo. The newest toast's action also answers ⌘Z / Ctrl+Z. */
  action?: { label: string; onAction: () => void };
  /** Default 6000 ms. A pointer moving over it, or focus in it, pauses the clock; a toast that appears under a resting pointer keeps its clock. */
  duration?: number;
  /** Runs once when the toast leaves without its action: on timeout, Esc, or when pushed out. Commit here. */
  onExpire?: () => void;
}

export interface ToastApi {
  show: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

interface Item {
  id: string;
  options: ToastOptions;
  closing: boolean;
}

const MAX_VISIBLE = 3;
/** The exit (animate-menu-out) runs 120 ms; the toast unmounts just after. */
const EXIT_MS = 140;
const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast needs a <ToastProvider> above it");
  return api;
}

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const live = useRef<Item[]>([]);
  const settled = useRef(new Set<string>());
  const seq = useRef(0);

  const settle = useCallback((id: string, how: "action" | "expire") => {
    if (settled.current.has(id)) return;
    const item = live.current.find((x) => x.id === id);
    if (!item) return;
    settled.current.add(id);
    live.current = live.current.filter((x) => x.id !== id);
    if (how === "action") item.options.action?.onAction();
    else item.options.onExpire?.();
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, closing: true } : x)));
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), EXIT_MS);
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${++seq.current}`;
      const oldest = live.current[0];
      if (live.current.length >= MAX_VISIBLE && oldest) settle(oldest.id, "expire");
      const item = { id, options, closing: false };
      live.current = [...live.current, item];
      setItems((xs) => [...xs, item]);
      return id;
    },
    [settle],
  );

  const api = useMemo<ToastApi>(() => ({ show, dismiss: (id) => settle(id, "expire") }), [show, settle]);

  // Leaving the page commits what is waiting: nothing is lost because a toast was still on screen.
  useEffect(() => {
    const onHide = () => {
      for (const item of [...live.current]) settle(item.id, "expire");
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [settle]);

  // ⌘Z / Ctrl+Z takes the newest toast's action.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.key.toLowerCase() !== "z") return;
      if (isEditable(event.target)) return;
      const newest = [...live.current].reverse().find((x) => x.options.action);
      if (!newest) return;
      event.preventDefault();
      settle(newest.id, "action");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [settle]);

  const newestWithAction = [...items].reverse().find((x) => !x.closing && x.options.action)?.id;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* From `lg` a sheet stands beside the page, so the toasts centre in what is left of the page column, never under the sheet's edge; from `wide` they sit in the top bar's band, over no content (a sheet opens under the bar). */}
      <div
        role="status"
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] mx-auto flex w-[min(400px,calc(100vw-32px))] flex-col gap-2 lg:left-sidebar lg:[:root:has([data-slot=sheet][data-state=open])_&]:right-[calc(var(--spacing-sheet)+12px)] wide:bottom-auto wide:top-1.5"
      >
        {items.map((item) => (
          <ToastView key={item.id} item={item} showHint={item.id === newestWithAction} onSettle={settle} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, showHint, onSettle }: { item: Item; showHint: boolean; onSettle: (id: string, how: "action" | "expire") => void }) {
  const { id, options, closing } = item;
  const duration = options.duration ?? 6000;
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);

  useEffect(() => {
    if (paused || closing) return;
    const started = Date.now();
    const timer = window.setTimeout(() => onSettle(id, "expire"), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [paused, closing, id, onSettle]);

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
  };

  // A pointer moving over it and focus in it only pause the clock (WCAG 2.2.1). Not a pointer that was
  // already resting where it appeared: then it would wait for ever, and its commit with it.
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-slot="toast"
      data-state={closing ? "closing" : "open"}
      onPointerMove={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={onBlur}
      onKeyDown={(event) => event.key === "Escape" && onSettle(id, "expire")}
      className={cx(
        "pointer-events-auto flex min-h-12 items-center justify-between gap-2 rounded-xl bg-popover p-1.5 text-popover-foreground shadow-popover",
        closing ? "animate-menu-out" : "animate-rise",
      )}
    >
      <span className="min-w-0 px-2 text-body">{options.message}</span>
      {options.action && (
        <span className="flex shrink-0 items-center gap-2">
          {showHint && <Kbd>{modKey("Z")}</Kbd>}
          <Button variant="surface" size="sm" onClick={() => onSettle(id, "action")}>
            {options.action.label}
          </Button>
        </span>
      )}
    </div>
  );
}
